import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import analyticsService from './services/analytics';
import SplashScreen from './components/common/SplashScreen/SplashScreen';
import PageTitle from './components/common/PageTitle/PageTitle';
import Header from './components/common/Header/Header';
import Footer from './components/common/Footer/Footer';
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Subscribe from './pages/Newsletter/NewsLetter';
import NewsDetail from './pages/NewsDetail/NewsDetail';
import Category from './pages/Category/Category';
import './styles/variables.css';
import './styles/global.css';
import './styles/animations.css';

// Analytics Wrapper Component
const AnalyticsWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    const path = location.pathname + location.search;
    const pageTitle = document.title;
    
    analyticsService.pageView(path, pageTitle);
    
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);

  return <>{children}</>;
};

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    // Initialize Google Analytics on app mount
    analyticsService.initialize();
    
    // Track initial page load time
    const startTime = performance.now();
    
    return () => {
      const loadTime = performance.now() - startTime;
      analyticsService.trackTiming('App', 'load', Math.round(loadTime), 'Initial Load');
    };
  }, []);

  useEffect(() => {
    // Wait for DOM to be fully loaded
    const checkContentReady = async () => {
      try {
        // Wait for fonts
        await document.fonts.ready;
        
        // Wait for images to load
        const images = Array.from(document.images);
        const imagePromises = images.map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise(resolve => {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error
          });
        });

        await Promise.all(imagePromises);

        // Additional delay to ensure content is rendered
        await new Promise(resolve => setTimeout(resolve, 300));

        setContentReady(true);
      } catch (error) {
        console.error('Error loading content:', error);
        // Track error
        analyticsService.trackError('Content Loading Error', window.location.pathname);
        setContentReady(true); // Continue anyway
      }
    };

    // Start checking after initial render
    const timer = setTimeout(() => {
      checkContentReady();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleLoadComplete = () => {
    // Only hide splash when content is also ready
    if (contentReady) {
      setIsLoading(false);
    } else {
      // Wait a bit more for content
      const checkAgain = setInterval(() => {
        if (contentReady) {
          setIsLoading(false);
          clearInterval(checkAgain);
        }
      }, 100);

      // Safety timeout - hide after 5 seconds max
      setTimeout(() => {
        setIsLoading(false);
        clearInterval(checkAgain);
      }, 5000);
    }
  };

  return (
    <ThemeProvider>
      {isLoading && <SplashScreen onLoadComplete={handleLoadComplete} />}
      <Router>
        <AnalyticsWrapper>
          <PageTitle />
          <div 
            className="App" 
            style={{ 
              opacity: isLoading ? 0 : 1, 
              transition: 'opacity 0.5s ease-in' 
            }}
          >
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/subscribe" element={<Subscribe />} />
                <Route path="/news/:slug" element={<NewsDetail />} />
                <Route path="/category/:category" element={<Category />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </AnalyticsWrapper>
      </Router>
    </ThemeProvider>
  );
};

export default App;